/**
 * Wrapper tipo-aware per il dettaglio preventivo (riordino IA 2026-07-18, DEC-103).
 *
 * Un preventivo e' unico in DB (discriminante `tipo`: 'acquisto' | 'vendita') ma
 * il dettaglio vive sotto due path diversi a seconda del ramo sidebar:
 *   - /preventivi/:id        -> gruppo Acquisti (modulo "Preventivi fornitori")
 *   - /vendite/preventivi/:id -> gruppo Vendite  (modulo "Preventivi clienti")
 *
 * Questi due componenti fanno una GET leggera (solo per leggere `tipo`) e, se il
 * tipo non corrisponde al ramo su cui sono montati, rediregono (replace) al path
 * gemello. Se il tipo corrisponde, renderizzano PreventivoDetail che rifara' la
 * propria fetch completa: doppia chiamata GET accettata per semplicita' (rischio
 * noto, vedi piano riordino T2 - traffico minimo, solo sull'apertura dettaglio).
 *
 * Cosi' i link vecchi (bookmark, FatturaDetail/DDTDetail/DocumentoDetail/
 * RichiestaDetail che navigano ancora a /preventivi/:id) continuano a funzionare
 * anche per i preventivi di vendita.
 */

import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { PreventivoDetail } from './PreventivoDetail';

type TipoPreventivo = 'acquisto' | 'vendita';

/** Legge solo il campo `tipo` del preventivo (per la decisione di redirect). */
function useTipoPreventivo(id: string | undefined): { tipo: TipoPreventivo | null; loading: boolean } {
  const [tipo, setTipo] = useState<TipoPreventivo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setTipo(null);
    fetch(`/api/v1/preventivi/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setTipo(data?.tipo ?? null);
      })
      .catch(() => {
        if (!cancelled) setTipo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return { tipo, loading };
}

const Caricamento = () => (
  <div className="p-6 text-center text-muted-foreground">Caricamento...</div>
);

/** Montato su /preventivi/:id (Acquisti). Se il preventivo e' di vendita, redirect a /vendite/preventivi/:id. */
export function PreventivoAcquistoRouter() {
  const { id } = useParams<{ id: string }>();
  const { tipo, loading } = useTipoPreventivo(id);

  if (loading) return <Caricamento />;
  if (tipo === 'vendita') return <Navigate to={`/vendite/preventivi/${id}`} replace />;
  return <PreventivoDetail />;
}

/** Montato su /vendite/preventivi/:id (Vendite). Se il preventivo e' di acquisto, redirect a /preventivi/:id. */
export function PreventivoVenditaRouter() {
  const { id } = useParams<{ id: string }>();
  const { tipo, loading } = useTipoPreventivo(id);

  if (loading) return <Caricamento />;
  if (tipo === 'acquisto') return <Navigate to={`/preventivi/${id}`} replace />;
  return <PreventivoDetail />;
}

/** Redirect per il link legacy /preventivi/vendita/nuovo -> nuovo path sotto Vendite. */
export function RedirectNuovoPreventivoVendita() {
  return <Navigate to="/vendite/preventivi/nuovo" replace />;
}
